/**
 * useCallOverlayQueue Hook
 *
 * Maintains a live queue snapshot from Socket.IO events and selects
 * the single queue item to show in the call overlay (dialing/ringing/active).
 * Priority: ringing > dialing > active; ties broken by order (lower first).
 *
 * v2 backend events:
 *   - created (new call — queue or incoming) on /call namespace
 *   - status (call status changed) on /call namespace
 */

import { useEffect, useState, useMemo } from 'react';

import { useSocketIOContext } from '@/shared/services/socketio';
import { SocketIONamespace } from '@/shared/services/socketio/types';

import { v2CallResponseToQueueModel } from '../mappers/v2CallResponseToQueueModel';

import type { QueueModel, CallStatus } from '../../domain/types';
import type { V2CallResponse, CallStatusEventData } from '@/shared/services/socketio/types';

const OVERLAY_STATUSES: CallStatus[] = ['dialing', 'ringing', 'active', 'forwarding'];
const TERMINAL_STATUSES: CallStatus[] = ['ended', 'closed', 'failed', 'cancelled', 'forwarded', 'followUp', 'follow_up'];

function statusPriority(status: string): number {
  const s = status.toLowerCase();
  if (s === 'ringing') return 0;
  if (s === 'dialing') return 1;
  if (s === 'active') return 2;
  if (s === 'forwarding') return 3;
  return 999;
}

function isOverlayStatus(status: string): boolean {
  return OVERLAY_STATUSES.some(s => s === status.toLowerCase());
}

function isTerminalStatus(status: string): boolean {
  return TERMINAL_STATUSES.some(s => s === status.toLowerCase());
}

/**
 * Selects the best queue item to display in the call overlay.
 * Only considers dialing, ringing, active; priority ringing > dialing > active; then by order.
 */
function selectOverlayQueue(queues: QueueModel[]): QueueModel | null {
  const candidates = queues.filter(q => isOverlayStatus(q.status));
  if (candidates.length === 0) return null;
  const sorted = [...candidates].sort((a, b) => {
    const pa = statusPriority(a.status);
    const pb = statusPriority(b.status);
    if (pa !== pb) return pa - pb;
    return a.order - b.order;
  });
  return sorted[0] ?? null;
}

export function useCallOverlayQueue(): { overlayQueue: QueueModel | null } {
  const { subscribe, isConnected } = useSocketIOContext();
  const [queues, setQueues] = useState<QueueModel[]>([]);

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = subscribe(event => {
      // Only handle events from /call namespace
      if (event.namespace !== SocketIONamespace.Call) return;

      // created — new call (queue or incoming)
      if (event.event === 'created') {
        const data = event.data as V2CallResponse;
        const mapped = v2CallResponseToQueueModel(data);
        if (isTerminalStatus(mapped.status)) {
          setQueues(prev => prev.filter(q => q.id !== mapped.id));
          return;
        }
        setQueues(prev => {
          const idx = prev.findIndex(q => q.id === mapped.id);
          if (idx !== -1) {
            const next = [...prev];
            next[idx] = mapped;
            return next;
          }
          const next = [...prev, mapped];
          next.sort((a, b) => a.order - b.order);
          return next;
        });
      }

      // status — call status changed (partial update)
      if (event.event === 'status') {
        const data = event.data as CallStatusEventData;

        setQueues(prev => {
          const idx = prev.findIndex(q => q.id === data.call_id);

          if (isTerminalStatus(data.status)) {
            if (idx === -1) return prev;
            const next = [...prev];
            next.splice(idx, 1);
            return next;
          }

          // Call not tracked yet (missed created event) —
          // add a placeholder so the overlay can appear when it reaches
          // an overlay status (dialing/ringing/active).
          if (idx === -1) {
            if (!isOverlayStatus(data.status)) return prev;
            const placeholder: QueueModel = {
              id: data.call_id,
              name: '',
              role: '',
              relationship: '',
              time_start: undefined,
              time_end: undefined,
              status: data.status as CallStatus,
              order: 0,
              callee: '',
              caller: {},
              tags: [],
              tier: '',
              has_transcript: false,
              is_fallback: false,
              has_call_detail: false,
            };
            return [...prev, placeholder];
          }

          const next = [...prev];
          next[idx] = { ...next[idx], status: data.status as CallStatus };
          return next;
        });
      }
    });

    return unsubscribe;
  }, [subscribe, isConnected]);

  const overlayQueue = useMemo(() => selectOverlayQueue(queues), [queues]);

  return { overlayQueue };
}
